import { LoanApplicationTabId } from 'app/pages/loanApplication/LoanApplication.constants';
import dayjs from 'dayjs';
import _ from 'lodash';

import { COMMON_REQUEST_DATE_FORMAT } from 'constants/date';
import { getConditionalObject, getConditionalValue, replaceEmptyWithNull } from 'utils/data';
import { getCommonDateBody } from 'utils/date';

import {
  DISCOUNT_PREMIUM_AMOUNT_DECIMAL_SCALE,
  DISCOUNT_PREMIUM_AMOUNT_FIELD,
  DISCOUNT_PREMIUM_PERCENT_FIELD,
  DISCOUNT_PREMIUM_PERCENT_DECIMAL_SCALE,
  POST_CLOSING_OPTION_LIST_TYPE_BY_FIELD,
} from '../PostClosing.constants';
import { getOptionsWithCurrentValue } from '../PostClosing.utils';

import {
  SECTION_FORM_CONFIG_MAP,
  SECTION_HREF_MAP,
  UNSECURED_HIDDEN_SECTIONS,
} from './EditDrawer.config';

export const getVisibleSectionHrefs = (isUnsecured = false) =>
  Object.values(SECTION_HREF_MAP).filter(
    value => !isUnsecured || !UNSECURED_HIDDEN_SECTIONS.has(value),
  );

export const resolvePostClosingSectionConfig = ({
  sectionKey,
  investorsOptions = [],
  servicingCompaniesOptions = [],
  isUnsecured = false,
  postClosingSelectOptions = {},
}) => {
  const configOrGetter = SECTION_FORM_CONFIG_MAP[sectionKey];

  if (!configOrGetter) {
    return { groups: [] };
  }

  return _.isFunction(configOrGetter)
    ? configOrGetter({
        investorsOptions,
        servicingCompaniesOptions,
        isUnsecured,
        postClosingSelectOptions,
      })
    : configOrGetter;
};

const FULL_PERCENT = 100;

// A formatted input keeps showing its last number when handed null; an empty string clears it and saves as null.
const EMPTY_INPUT_VALUE = '';

const isBlank = value => _.isNil(value) || value === '';

// Editing either Discount/Premium field rewrites the other, the way the Down Payment fields do. Second liens use the
// server's basis; other products have only the principal balance. With no basis to divide by, the percent is cleared
// so the typed dollars are what saves.
export const getDiscountPremiumCounterpart = ({ name, value, postClosing }) => {
  const basis = postClosing?.discountPremiumBasis ?? postClosing?.principalBalance;

  if (name === DISCOUNT_PREMIUM_PERCENT_FIELD) {
    if (isBlank(value)) {
      return { [DISCOUNT_PREMIUM_AMOUNT_FIELD]: EMPTY_INPUT_VALUE };
    }

    return _.isNil(basis)
      ? {}
      : {
          [DISCOUNT_PREMIUM_AMOUNT_FIELD]: _.round(
            (Number(value) * basis) / FULL_PERCENT,
            DISCOUNT_PREMIUM_AMOUNT_DECIMAL_SCALE,
          ),
        };
  }

  if (isBlank(value) || !Number(basis)) {
    return { [DISCOUNT_PREMIUM_PERCENT_FIELD]: EMPTY_INPUT_VALUE };
  }

  return {
    [DISCOUNT_PREMIUM_PERCENT_FIELD]: _.round(
      (Number(value) / basis) * FULL_PERCENT,
      DISCOUNT_PREMIUM_PERCENT_DECIMAL_SCALE,
    ),
  };
};

export const buildPostClosingApplicationData = postClosing => {
  if (!postClosing) {
    return {};
  }

  return {
    ...postClosing,
    ...postClosing.finalDocuments,
    ...postClosing.shipperMilestone,
    ...postClosing.mersInformation,
    ...postClosing.flippedUnsalable,
  };
};

export const getPostClosingSelectOptions = ({ optionsByListType, postClosing }) => {
  const applicationData = buildPostClosingApplicationData(postClosing);

  return _.mapValues(POST_CLOSING_OPTION_LIST_TYPE_BY_FIELD, (listType, fieldName) =>
    getOptionsWithCurrentValue(optionsByListType[listType], applicationData[fieldName]),
  );
};

const buildInvestorConditionsPayload = investorConditions =>
  (investorConditions ?? []).map(condition => ({
    id: condition.id,
    conditionText: condition.conditionText,
    dateAdded: getConditionalValue(
      condition.dateAdded,
      dayjs(condition.dateAdded).format(COMMON_REQUEST_DATE_FORMAT),
    ),
    dateResponded: getConditionalValue(
      condition.dateResponded,
      dayjs(condition.dateResponded).format(COMMON_REQUEST_DATE_FORMAT),
    ),
    dateCleared: getConditionalValue(
      condition.dateCleared,
      dayjs(condition.dateCleared).format(COMMON_REQUEST_DATE_FORMAT),
    ),
  }));

const buildSectionPayload = ({ sectionKey, formData, postClosing }) => {
  switch (sectionKey) {
    case SECTION_HREF_MAP.SHIPPING:
      return replaceEmptyWithNull({
        collateralTrackingNumberToWarehouseBank: formData.collateralTrackingNumberToWarehouseBank,
        collateralTrackingNumberToInvestor: formData.collateralTrackingNumberToInvestor,
        noteTrackingNumberToInvestor: formData.noteTrackingNumberToInvestor,
        noteTrackingNumberToWarehouse: formData.noteTrackingNumberToWarehouse,
        collateralShippedToWarehouseDate: getCommonDateBody(
          formData.collateralShippedToWarehouseDate,
        ),
        collateralReceivedToWarehouseDate: getCommonDateBody(
          formData.collateralReceivedToWarehouseDate,
        ),
        collateralShippedToInvestorDate: getCommonDateBody(
          formData.collateralShippedToInvestorDate,
        ),
        collateralReceivedToInvestorDate: getCommonDateBody(
          formData.collateralReceivedToInvestorDate,
        ),
        noteShippedToWarehouseDate: getCommonDateBody(formData.noteShippedToWarehouseDate),
        noteReceivedToWarehouseDate: getCommonDateBody(formData.noteReceivedToWarehouseDate),
        noteShippedToInvestorDate: getCommonDateBody(formData.noteShippedToInvestorDate),
        noteReceivedToInvestorDate: getCommonDateBody(formData.noteReceivedToInvestorDate),
        trackingNumberTitleCompanyToLender: formData.trackingNumberTitleCompanyToLender,
        shippedTitleCompanyToLenderDate: getCommonDateBody(
          formData.shippedTitleCompanyToLenderDate,
        ),
        receivedTitleCompanyToLenderDate: getCommonDateBody(
          formData.receivedTitleCompanyToLenderDate,
        ),
        originalMortgageTrackingNumberToInvestor: formData.originalMortgageTrackingNumberToInvestor,
        noteTrackingNumberFromOriginalInvestor: formData.noteTrackingNumberFromOriginalInvestor,
      });
    case SECTION_HREF_MAP.INVESTOR:
      return replaceEmptyWithNull({
        investorId: formData.investorId?.value,
        commitmentNumber: formData.commitmentNumber,
        conditions: formData.conditions,
        dueDate: getCommonDateBody(formData.dueDate),
        investorLoanNumber: formData.investorLoanNumber,
        investorPackageUploadedDate: getCommonDateBody(formData.investorPackageUploadedDate),
      });
    case SECTION_HREF_MAP.ACCOUNTING:
      return replaceEmptyWithNull({
        discount: formData.discount,
        discountPremiumPercent: formData.discountPremiumPercent,
        escrowBalance: formData.escrowBalance,
        firstDueToInvestor: getCommonDateBody(formData.firstDueToInvestor),
        firstPayment: getCommonDateBody(formData.firstPayment),
        fundingFee: formData.fundingFee,
        interest: formData.interest,
        principalBalance: postClosing?.principalBalance,
        principalPurchased: formData.principalPurchased,
        taxFee: formData.taxFee,
        investorWireAmount: formData.investorWireAmount,
      });
    case SECTION_HREF_MAP.SERVICER:
      return replaceEmptyWithNull({
        servicer: formData.servicer?.value,
        servicerLoanNumber: formData.servicerLoanNumber,
        transmitDate: getCommonDateBody(formData.transmitDate),
        min: formData.min,
        servicingOwnershipType: formData.servicingOwnershipType?.value,
        servicingStatus: formData.servicingStatus?.value,
        servicingStartDate: getCommonDateBody(formData.servicingStartDate),
        servicingEndDate: getCommonDateBody(formData.servicingEndDate),
        poolNumber: formData.poolNumber,
        investorType: formData.investorType?.value,
      });
    case SECTION_HREF_MAP.PAYMENT:
      return replaceEmptyWithNull({
        autoDebitPayment: formData.autoDebitPayment,
      });
    case SECTION_HREF_MAP.FINAL_DOCUMENTS:
      return {
        finalDocuments: replaceEmptyWithNull({
          closedPackageReceivedDate: getCommonDateBody(formData.closedPackageReceivedDate),
          postClosingReviewCompleted: formData.postClosingReviewCompleted ?? null,
          investorLockDate: getCommonDateBody(formData.investorLockDate),
          investorLockExpiration: getCommonDateBody(formData.investorLockExpiration),
          transmissionConfirmationDate: getCommonDateBody(formData.transmissionConfirmationDate),
          correctionInProcessDate: getCommonDateBody(formData.correctionInProcessDate),
          unrecordedMortgageReceivedDate: getCommonDateBody(
            formData.unrecordedMortgageReceivedDate,
          ),
          copyOfRecordedMortgageReceivedDate: getCommonDateBody(
            formData.copyOfRecordedMortgageReceivedDate,
          ),
          recordedMortgageUploadedDate: getCommonDateBody(formData.recordedMortgageUploadedDate),
          titlePolicyUploadedDate: getCommonDateBody(formData.titlePolicyUploadedDate),
          tpMtgInFileCabinetDate: getCommonDateBody(formData.tpMtgInFileCabinetDate),
          noteReturnedDate: getCommonDateBody(formData.noteReturnedDate),
          noteReturnedReason: formData.noteReturnedReason ?? null,
          finalDocumentsComments: formData.finalDocumentsComments ?? null,
          noteMissingFromClosedPackage: formData.noteMissingFromClosedPackage ?? null,
          postClosingReviewDefects: formData.postClosingReviewDefects ?? null,
          preppedForTransmissionDate: getCommonDateBody(formData.preppedForTransmissionDate),
          loanTransmittedDate: getCommonDateBody(formData.loanTransmittedDate),
          transmissionComments: formData.transmissionComments ?? null,
          mortgagePaidOffTitlePolicyNotRequired:
            formData.mortgagePaidOffTitlePolicyNotRequired ?? null,
          titlePolicyRequestedDate: getCommonDateBody(formData.titlePolicyRequestedDate),
          titlePolicyReceivedDate: getCommonDateBody(formData.titlePolicyReceivedDate),
          titlePolicyCorrectionInProcessDate: getCommonDateBody(
            formData.titlePolicyCorrectionInProcessDate,
          ),
          titlePolicyDeliveredToInvestorDate: getCommonDateBody(
            formData.titlePolicyDeliveredToInvestorDate,
          ),
          titlePolicyNotes: formData.titlePolicyNotes ?? null,
          recordedMortgageRequestedDate: getCommonDateBody(formData.recordedMortgageRequestedDate),
          originalMortgageDeliveredDate: getCommonDateBody(formData.originalMortgageDeliveredDate),
          recordedMortgageDeliveredToInvestorDate: getCommonDateBody(
            formData.recordedMortgageDeliveredToInvestorDate,
          ),
          recordedMortgageNotes: formData.recordedMortgageNotes ?? null,
        }),
      };
    case SECTION_HREF_MAP.SHIPPER_MILESTONE:
      return {
        shipperMilestone: replaceEmptyWithNull({
          closingPackageUploaded: formData.closingPackageUploaded ?? null,
          creditPackageUploaded: formData.creditPackageUploaded ?? null,
        }),
      };
    case SECTION_HREF_MAP.MERS:
      return replaceEmptyWithNull({
        mersTransferBatchNumber: formData.mersTransferBatchNumber,
        mersTransferDate: getCommonDateBody(formData.mersTransferDate),
        mersTransferAcceptedDate: getCommonDateBody(formData.mersTransferAcceptedDate),
        minRegistrationReversedDate: getCommonDateBody(formData.minRegistrationReversedDate),
        minRegistrationReversalReason: formData.minRegistrationReversalReason,
        minDeactivatedDate: getCommonDateBody(formData.minDeactivatedDate),
        minDeactivationType: formData.minDeactivationType?.value,
      });
    case SECTION_HREF_MAP.FLIPPED_UNSALABLE:
      return replaceEmptyWithNull({
        flippedToNewInvestorDate: getCommonDateBody(formData.flippedToNewInvestorDate),
        markedUnsalableDate: getCommonDateBody(formData.markedUnsalableDate),
        noteReturnRequestedDate: getCommonDateBody(formData.noteReturnRequestedDate),
        unsalableResponsibleParty: formData.unsalableResponsibleParty?.value,
        unsalableReason: formData.unsalableReason?.value,
        purchaseDenialReason: formData.purchaseDenialReason,
      });
    default:
      return null;
  }
};

export const buildPostClosingRequestBody = ({ loanId, formData, postClosing, sectionKeys }) => {
  const getSectionPayloadOrNull = sectionKey =>
    sectionKeys.has(sectionKey) ? buildSectionPayload({ sectionKey, formData, postClosing }) : null;
  const hasInvestorSection = sectionKeys.has(SECTION_HREF_MAP.INVESTOR);

  return {
    loanId,
    shippingInfo: getSectionPayloadOrNull(SECTION_HREF_MAP.SHIPPING),
    closingInvestor: getSectionPayloadOrNull(SECTION_HREF_MAP.INVESTOR),
    investorConditions: hasInvestorSection
      ? buildInvestorConditionsPayload(postClosing?.investorConditions)
      : null,
    accountingInformation: getSectionPayloadOrNull(SECTION_HREF_MAP.ACCOUNTING),
    servicerInfo: getSectionPayloadOrNull(SECTION_HREF_MAP.SERVICER),
    paymentInformation: getSectionPayloadOrNull(SECTION_HREF_MAP.PAYMENT),
    mersInformation: getSectionPayloadOrNull(SECTION_HREF_MAP.MERS),
    flippedUnsalable: getSectionPayloadOrNull(SECTION_HREF_MAP.FLIPPED_UNSALABLE),
    ...getConditionalObject(sectionKeys.has(SECTION_HREF_MAP.FINAL_DOCUMENTS), () =>
      buildSectionPayload({ sectionKey: SECTION_HREF_MAP.FINAL_DOCUMENTS, formData, postClosing }),
    ),
    ...getConditionalObject(sectionKeys.has(SECTION_HREF_MAP.SHIPPER_MILESTONE), () =>
      buildSectionPayload({
        sectionKey: SECTION_HREF_MAP.SHIPPER_MILESTONE,
        formData,
        postClosing,
      }),
    ),
    requestMetadata: { tabId: LoanApplicationTabId.CLOSING },
  };
};
